import { UseFormSetValue, FieldValues, Path, PathValue } from 'react-hook-form';
import SearchCheckGroup, {
  InfiniteSearchInputProps,
} from '@/components/ui/custom/dataInput/SearchCheckbox';
import { Model as BaseModel } from '@/models/Model';
import useToggle from '@/data/hook/useToggle';
import { BaseFilters } from '@/models/Service';
import { forwardRef, useEffect } from 'react';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { CheckGroupContainer } from '@/components/ui/custom/dataInput/CheckGroup';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import IconButton from '@/components/ui/custom/buttons/IconButton';
type FilterActionsProps = {
  onClickSearch?: () => void;
  onClickClean?: () => void;
  showClean?: boolean;
  showSearch?: boolean;
};

export function createFilterHandlers<T extends FieldValues>(
  setValue: UseFormSetValue<T>,
) {
  type PathNumber = PathValue<T, Path<T>>;
  const handleClean = (key: Path<T>): void => {
    setValue(key, [] as PathNumber);
  };
  const handleTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: Path<T>,
    arr?: number[],
  ): void => {
    const { checked } = e.target;
    const value = Number(e.target.value);
    const newTypes = checked
      ? [...(arr || []), value]
      : arr?.filter(type => Number(type) !== value);
    setValue(key, newTypes as PathNumber);
  };

  return { handleClean, handleTypeChange };
}
const FilterActions = ({
  showClean,
  showSearch,
  onClickClean,
  onClickSearch,
}: FilterActionsProps) => {
  return (
    <div className="flex gap-2 items-center">
      <ConditionalRenderer condition={showSearch}>
        <IconButton
          icon={<MagnifyingGlassIcon className="w-4 text-base-content" />}
          onClick={onClickSearch}
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={showClean}>
        <IconButton
          icon={
            <XMarkIcon className="w-4 text-base-content" onClick={onClickClean} />
          }
        />
      </ConditionalRenderer>
    </div>
  );
};

type CreateFilterProps<
  T extends BaseFilters = BaseFilters,
  K extends BaseModel = BaseModel,
> = {
  badgeCount?: number;
  onClean?: () => void;
} & InfiniteSearchInputProps<T, K>;

export const CreateFilter = forwardRef<HTMLInputElement, CreateFilterProps>(
  ({ badgeCount = 0, onClean, title, ...inputProps }, ref) => {
    const { isOpen, toggle, open } = useToggle();
    const { isOpen: search, toggle: toogleSearch } = useToggle();

    useEffect(() => {
      if (search) {
        open();
      }
    }, [search, open]);

    return (
      <CheckGroupContainer
        title={title || ''}
        badgeCount={badgeCount}
        isOpen={isOpen}
        onToggle={toggle}
        actions={
          <FilterActions
            showClean={badgeCount > 0}
            showSearch={inputProps.showSearch}
            onClickSearch={toogleSearch}
            onClickClean={onClean}
          />
        }
      >
        <SearchCheckGroup
          {...inputProps}
          showSearch={inputProps.showSearch && search}
          ref={ref}
        />
      </CheckGroupContainer>
    );
  },
) as <Filter extends BaseFilters, Model extends BaseModel>(
  props: CreateFilterProps<Filter, Model> & {
    ref?: React.Ref<HTMLInputElement>;
  },
) => JSX.Element;
